<template>
  <article id="estimating-pi" class="post">
    <post-header v-bind:post="post"></post-header>

    <div class="body-text">
      <div id="simulation">
        <div id="simulation-viz"></div>
        <div id="simulation-stats">
          <div id="controls-holder">
            <label for="reps-input">
              Number of points:
              <input id="reps-input" type="number" min="1" step="1" v-model="reps">
            </label>
            <button v-if="!running" id="start-button" @click="start">Start</button>
            <button v-else id="stop-button" @click="stop">Stop</button>
          </div>

          <table>
            <thead>
              <tr>
                <th>Points</th>
                <th>Inside</th>
                <th>Percent</th>
                <th>Estimate</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="stats.total === 0">
                <td>0</td>
                <td>0</td>
                <td>N/A</td>
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr v-else class="current">
                <td>{{ stats.total }}</td>
                <td>{{ stats.inside }}</td>
                <td>{{ stats.percent }}%</td>
                <td>{{ stats.estimate }}</td>
                <td :style="{ backgroundColor: stats.color }">{{ stats.error }}</td>
              </tr>
              <tr v-for="r in stats.rounds" :key="r.total">
                <td>{{ r.total }}</td>
                <td>{{ r.inside }}</td>
                <td>{{ r.percent }}%</td>
                <td>{{ r.estimate }}</td>
                <td :style="{ backgroundColor: r.color }">{{ r.error }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="section-divider"></div>

      <p>
        Above is a visual representation of the Monte Carlo method for estimating
        the value of &pi;.
      </p>
      <p>
        By inscribing a circle upon a square as shown above, we guarantee that the
        diameter of the circle is the same length as the sides of the square. So,
        the area of the circle is
      </p>
      <p>
        A
        <sub>circle</sub> = &pi;r
        <sup>2</sup>
      </p>
      <p>and the area of the square is</p>
      <p>
        A
        <sub>square</sub> =
        (2r)
        <sup>2</sup>
      </p>
      <p>where r is the same in both expressions.</p>
      <p>From there, we can get:</p>
      <p>
        A
        <sub>circle</sub> / &pi; = r
        <sup>2</sup> = A
        <sub>square</sub>
        / 4
      </p>
      <p>OR</p>
      <p>
        &pi; =
        4A
        <sub>circle</sub> / A
        <sub>square</sub>
      </p>

      <p>
        This tells us that when we generate points at random locations within the square,
        there is a &pi;/4 (~0.7854) chance that a point falls within the circle as well.
        And by substituting the counts of points in the circle and square in for the areas
        in the above equation, we can calculate an approximation of &pi;. The more points we
        generate, the closer our approximation will be.
      </p>

      <h4 class="text-heading">Sources and Credits</h4>
      <p>
        Built using D3. Inspired by
        <a
          href="https://www.reddit.com/r/dataisbeautiful/comments/aa23p3/first_10000_iterations_of_a_monte_carlo/"
        >this post</a>
        by u/Toemel on reddit.
      </p>
    </div>
  </article>
</template>

<script>
import * as d3 from 'd3';

import PostHeader from '../../post-sections/post-header.vue';
import Simulation from './js/simulation.js';

export default {
    name: 'estimating-pi',
    props: ['post'],
    components: {
        'post-header': PostHeader
    },
    data() {
        return {
            reps: 10000,
            running: false,
            stats: {
                total: 0,
                inside: 0,
                percent: 'N/A',
                estimate: '???',
                error: 'N/A',
                rounds: []
            }
        };
    },
    mounted() {
        this.simulation = new Simulation(
            '#simulation-viz',
            this.updateDataEvery,
            this.updateDataRound,
            () => (this.running = false)
        );

        d3.selectAll('#simulation')
            .selectAll('*')
            .attr(this.$options._scopeId, '');
    },
    methods: {
        start() {
            const reps = Number(this.reps);
            this.simulation.start(
                reps > this.stats.total ? reps : this.stats.total + reps,
                Math.ceil(reps / 10),
                Math.ceil(reps / 10)
            );
            this.running = true;
        },
        stop() {
            this.simulation.stop();
            this.running = false;
        },
        updateDataEvery(inside, outside) {
            this.stats.total = inside + outside;
            this.stats.inside = inside;
            this.stats.percent = ((100 * inside) / this.stats.total).toFixed(2);
            this.stats.estimate = ((4 * inside) / this.stats.total).toFixed(5);

            const error = this.stats.estimate - Math.PI;
            this.stats.error = error.toFixed(5);
            this.stats.color = 'rgba(0,0,0,0)';
        },
        updateDataRound() {
            if (this.stats.error > 0) {
                this.stats.color = `hsl(120, 50%, ${100 *
                    Math.max(1 - this.stats.error / 0.25, 0.5)}%)`;
            } else {
                this.stats.color = `hsl(0, 50%, ${100 *
                    Math.max(1 + this.stats.error / 0.25, 0.5)}%)`;
            }

            this.stats.rounds.unshift({
                ...this.stats,
                rounds: undefined
            });
        }
    }
};
</script>

<style scoped>
#simulation {
    width: 100%;
    height: 500px;
    margin: auto;
    position: relative;
}

#simulation-viz,
#simulation-stats {
    width: 50%;
    height: 100%;
    margin: 0;
    padding: 0;

    position: absolute;
    top: 0;
}

#simulation-viz {
    left: 0;
}

#simulation-stats {
    right: 0;
}

#simulation-stats table {
    width: 90%;
    margin: auto;
}

#simulation-stats table th,
#simulation-stats table td {
    width: 82px;
}

#simulation-stats table td:nth-child(4) {
    background-color: #eee;
}

#simulation-stats table tbody {
    display: block;
    max-height: 425px;
    overflow-y: auto;
}

#simulation-stats table thead {
    display: block;
}

.current {
    background-color: '#fdff91';
}

#controls-holder {
    width: 90%;
    margin: auto;
}

#reps-input {
    width: 8rem;
    text-align: center;
    border: none;
    border-bottom: 1px solid #222;
    margin: 0;
    padding: 0.5rem 1.5rem;
    font-size: 1.4rem;
}

#start-button,
#stop-button {
    border: 1px solid #222;
    margin: 0 0 0 1rem;
    padding: 0.5rem 1.5rem;
    font-size: 1.4rem;
}

#start-button {
    background-color: green;
}

#stop-button {
    background-color: red;
}

@media (max-width: 750px) {
    #simulation {
        width: 100%;
        height: unset;
        margin: auto;
        position: relative;
        overflow: auto;
    }

    #simulation-viz,
    #simulation-stats {
        width: 100%;
        height: unset;
        position: unset;
        top: unset;
        left: unset;
        right: unset;
    }

    #simulation-stats {
        margin-top: 2rem;
        text-align: center;
    }

    #start-button,
    #stop-button {
        margin: 1rem 0 0 0;
    }

    #simulation-stats table {
        margin: 1rem auto;
    }
}
</style>
